'use client';

import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import GenericFormModal from '../form/GenericFormModal';
import { GENERIC_FORM_FIELD_TYPE } from '../form/GenericForm';
import AddIcon from '@mui/icons-material/Add';
import LinkOutIcon from '@mui/icons-material/OpenInNew';
import { useNotification } from '@/contexts/NotificationContext';
import {
  useAssignTomVendingMachineToLocation,
  useCreateTomVendingMachine,
  useRemoveTomVendingMachineFromLocation,
  useTomVendingMachinesAssignable,
  useTomVendingMachinesList,
  type TomVendingMachine,
} from '@/hooks/useTomVendingMachine';

interface TomsVendingLocationMachinesSectionProps {
  locationId: number;
  manageOpen: boolean;
  onManageOpenChange: (open: boolean) => void;
}

const TomsVendingLocationMachinesSection: React.FC<TomsVendingLocationMachinesSectionProps> = ({
  locationId,
  manageOpen,
  onManageOpenChange,
}) => {
  const [createOpen, setCreateOpen] = useState(false);

  const { data: atLocation = [], refetch: refetchAtLocation } = useTomVendingMachinesList(locationId);
  const sortedAtLocation = useMemo(
    () =>
      [...atLocation].sort((a, b) => a.sortOrder - b.sortOrder || a.assetId.localeCompare(b.assetId)),
    [atLocation],
  );

  const {
    data: assignable = [],
    refetch: refetchAssignable,
  } = useTomVendingMachinesAssignable(locationId, manageOpen);

  const createMutation = useCreateTomVendingMachine();
  const assignMutation = useAssignTomVendingMachineToLocation();
  const removeMutation = useRemoveTomVendingMachineFromLocation();
  const { showSuccess, showError } = useNotification();

  const formFields = useMemo(
    () => [
      {
        groupName: 'Vending machine',
        fields: [
          {
            label: 'Order',
            name: 'sortOrder',
            type: GENERIC_FORM_FIELD_TYPE.NUMBER,
            required: true,
            columns: 6,
          },
          {
            label: 'Asset ID',
            name: 'assetId',
            type: GENERIC_FORM_FIELD_TYPE.TEXT,
            required: true,
            columns: 6,
          },
          { label: 'Title', name: 'title', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: false, columns: 12 },
        ],
      },
    ],
    [],
  );

  const handleCreateSubmit = (values: Record<string, unknown>) => {
    const sortOrder =
      values.sortOrder === '' || values.sortOrder === undefined
        ? 0
        : parseInt(String(values.sortOrder), 10);
    if (Number.isNaN(sortOrder)) {
      showError('Order must be a number');
      return;
    }
    createMutation.mutate(
      {
        title: values.title,
        sortOrder,
        assetId: values.assetId,
        locationId,
      },
      {
        onSuccess: () => {
          showSuccess('Vending machine created');
          setCreateOpen(false);
          void refetchAtLocation();
          void refetchAssignable();
        },
        onError: (e: { message?: string }) => showError(e?.message || 'Failed to create'),
      },
    );
  };

  const assignMachine = (m: TomVendingMachine) => {
    assignMutation.mutate(
      { machineId: m.id, locationId },
      {
        onSuccess: () => {
          showSuccess(`Assigned ${m.assetId} to this location`);
          void refetchAtLocation();
          void refetchAssignable();
        },
        onError: (e: { message?: string }) => showError(e?.message || 'Failed to assign'),
      },
    );
  };

  const removeFromLocation = (m: TomVendingMachine) => {
    if (
      !window.confirm(
        `Remove ${m.assetId} from this location? On-machine stock must be zero. The machine can be assigned elsewhere later.`,
      )
    ) {
      return;
    }
    removeMutation.mutate(
      { machineId: m.id },
      {
        onSuccess: () => {
          showSuccess(`${m.assetId} removed from this location`);
          void refetchAtLocation();
          void refetchAssignable();
        },
        onError: (e: { message?: string }) => showError(e?.message || 'Failed to remove'),
      },
    );
  };

  const modals = (
    <>
      <GenericFormModal
        visible={createOpen}
        onDismiss={() => setCreateOpen(false)}
        onSubmit={handleCreateSubmit}
        initialValues={{
          title: '',
          sortOrder: sortedAtLocation.length,
          assetId: '',
        }}
        fieldGroups={formFields}
        title="Add vending machine"
      />

      <Dialog open={manageOpen} onClose={() => onManageOpenChange(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Vending machines at this location</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create a new machine for this site, assign an existing machine (including from another
            location), or remove a machine from this location when its on-machine stock is zero.
          </Typography>

          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            onClick={() => {
              onManageOpenChange(false);
              setCreateOpen(true);
            }}
            sx={{ textTransform: 'none', mb: 2 }}
          >
            New vending machine
          </Button>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            At this location ({sortedAtLocation.length})
          </Typography>
          {sortedAtLocation.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No vending machines yet.
            </Typography>
          ) : (
            <List dense disablePadding sx={{ mb: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
              {sortedAtLocation.map((m) => (
                <ListItem
                  key={m.id}
                  secondaryAction={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <IconButton
                        component={RouterLink}
                        to={`/app/toms-vending/locations/${locationId}/machines/${m.id}`}
                        size="small"
                        aria-label="Open machine"
                      >
                        <LinkOutIcon fontSize="small" />
                      </IconButton>
                      <Button
                        size="small"
                        color="warning"
                        onClick={() => removeFromLocation(m)}
                        disabled={removeMutation.isPending}
                        sx={{ textTransform: 'none' }}
                      >
                        Remove
                      </Button>
                    </Box>
                  }
                  sx={{ pr: 18 }}
                >
                  <ListItemText
                    primary={`${m.assetId}${m.title?.trim() ? ` · ${m.title.trim()}` : ''}`}
                    secondary={m.sortOrder != null ? `Order ${m.sortOrder}` : undefined}
                  />
                </ListItem>
              ))}
            </List>
          )}

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Assign existing machine
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Unassigned machines or machines at other locations. Inventory and history stay with the
            machine when you move it.
          </Typography>
          <Autocomplete
            options={assignable}
            getOptionLabel={(o) =>
              `${o.assetId}${o.title?.trim() ? ` — ${o.title.trim()}` : ''}`
            }
            groupBy={(o) => (o.location ? `At: ${o.location.name ?? 'Location ' + o.location.id}` : 'Unassigned')}
            renderOption={(props, option) => (
              <li {...props}>
                <Box>
                  <Typography variant="body2">
                    {option.assetId}
                    {option.title?.trim() ? ` — ${option.title.trim()}` : ''}
                  </Typography>
                  {option.location?.name && (
                    <Typography variant="caption" color="text.secondary">
                      Currently: {option.location.name}
                    </Typography>
                  )}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Search asset ID or title" size="small" placeholder="Type to search…" />
            )}
            onChange={(_, v) => {
              if (v) assignMachine(v);
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => onManageOpenChange(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );

  return modals;
};

export default TomsVendingLocationMachinesSection;
